import React, { useEffect, useMemo, useState } from "react";
import muteIcon from "../../../assets/images/mute.svg";
import { useCloseSession } from "../../../hooks/useEndSession";
import { usePopup } from "../../../hooks/usePopup";
import { useLocalization } from "../../../stores/localizationContext";
import { useLiveSessionStore } from "../../../stores/useLiveSessionStore";
import { ConfirmPopup } from "../../Popups/ConfirmPopup";
import { EndSessionTimer } from "./EndSessionTimer";

const actions = [
  { class: "times-icon", btnClass: "btn-red", label: "Close", type: "icon" },
  { class: "microphone-icon", btnClass: "btn-blue", label: "Mic", type: "mic" },
];

export const ToolbarActions: React.FC = () => {
  // ✅ State from Zustand (pure state, no side effects)
  const micStatus = useLiveSessionStore((state) => state.micStatus);
  const showMicNotification = useLiveSessionStore(
    (state) => state.showMicNotification
  );
  const setMicStatus = useLiveSessionStore((state) => state.setMicStatus);
  const setShowMicNotification = useLiveSessionStore(
    (state) => state.setShowMicNotification
  );
  const cycleNextMicStatus = useLiveSessionStore(
    (state) => state.cycleNextMicStatus
  );
  const isEngineLoaded = useLiveSessionStore((state) => state.isEngineLoaded);
  const setIsMicOn = useLiveSessionStore((state) => state.setIsMicOn);
  const [showCloseSessionPopup, setShowCloseSessionPopup] = useState(false);
  const { handleCloseSession } = useCloseSession();
  const { t } = useLocalization();
  // ✅ Engine communication (side effect)
  const sendMessageToEngine = useLiveSessionStore(
    (state) => state.sendMessageToEngine
  );

  // Send initial mute action to engine when it's ready
  useEffect(() => {
    if (!isEngineLoaded) {
      return;
    }

    sendMessageToEngine("addSelfSessionActionLog", {
      resource: "mic",
      action: "mute",
    });
  }, [isEngineLoaded, sendMessageToEngine]);

  const getMicNotification = () => {
    switch (micStatus) {
      case "muted":
        return "You are muted";
      case "connected":
        return "Microphone active";
      default:
        return "";
    }
  };

  const handleMicClick = () => {
    // ✅ Get next status from slice (pure function, no side effects)
    const nextStatus = cycleNextMicStatus();

    // ✅ Update state
    setMicStatus(nextStatus);
    setShowMicNotification(true);

    // ✅ Control LiveKit microphone based on status
    if (nextStatus === "connected") {
      setIsMicOn(true);
    } else if (nextStatus === "muted") {
      setIsMicOn(false);
    }
    // ✅ Send to engine only if loaded (side effect in component)
    if (!isEngineLoaded) {
      console.warn(
        "[ToolbarActions] Engine not loaded yet, skipping mic action log"
      );
    } else {
      if (nextStatus === "muted") {
        sendMessageToEngine("addSelfSessionActionLog", {
          resource: "mic",
          action: "mute",
        });
      } else if (nextStatus === "connected") {
        sendMessageToEngine("addSelfSessionActionLog", {
          resource: "mic",
          action: "unmute",
        });
      }
    }

    // Hide notification after 2 seconds
    setTimeout(() => setShowMicNotification(false), 2000);
  };

  // Memoize the popup content to prevent unnecessary re-renders
  const closeSessionPopupContent = useMemo(() => {
    if (!showCloseSessionPopup) return null;

    return (
      <ConfirmPopup
        icon="times-icon"
        onConfirm={handleCloseSession}
        onCancel={() => setShowCloseSessionPopup(false)}
        title={t("end_session_confirmation_title")}
        message={t("end_session_confirmation_message")}
        confirmText={t("yes_label")}
        cancelText={t("no_label")}
      />
    );
  }, [showCloseSessionPopup, t]);

  // Setup popup with KickoutPopup component
  const CloseSessionPopupComponent = usePopup(closeSessionPopupContent);

  const openCloseSessionPopup = () => {
    setShowCloseSessionPopup(true);
  };

  return (
    <>
      <div className="toolbar__actions">
        {actions.map((action) => (
          <button
            key={action.label}
            className={`btn app-btn-icon ${
              action.type === "mic" && micStatus === "muted"
                ? "btn-white"
                : action.btnClass
            }`}
            aria-label={action.label}
            onClick={
              action.label === "Mic" ? handleMicClick : openCloseSessionPopup
            }
          >
            {action.type === "mic" ? (
              micStatus === "muted" ? (
                <img src={muteIcon} alt={action.label} style={{ height: 24 }} />
              ) : (
                <i className="f-icon fa-light microphone-icon"></i>
              )
            ) : (
              <i className={`f-icon fa-light ${action.class}`}></i>
            )}
          </button>
        ))}
        <div className="toolbar__time">
          <EndSessionTimer />
        </div>
        <div
          className={`notification${
            micStatus === "muted" ? " notification-warning" : ""
          }`}
          style={{ display: showMicNotification ? "block" : "none" }}
        >
          <div className="notification-content">
            {getMicNotification()}
            {micStatus === "muted" && <img src={muteIcon} alt="Muted" />}
          </div>
        </div>
      </div>
      {<CloseSessionPopupComponent /> }
    </>
  );
};
